import {
  Trophy, TrendingUp, TrendingDown, Flame, ShieldOff, ShieldX, Shield, Crown,
  RotateCcw, Target, Sofa, Zap, GitMerge, ArrowDown, Bomb, Frown, Megaphone,
  Sparkles, Pencil, Trash2, Send, type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { fmtUSD, type LeagueBonus } from './types';

const ICON_MAP: Record<string, LucideIcon> = {
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  'flame': Flame,
  'shield-off': ShieldOff,
  'shield-x': ShieldX,
  'shield': Shield,
  'crown': Crown,
  'rotate-ccw': RotateCcw,
  'target': Target,
  'sofa': Sofa,
  'zap': Zap,
  'git-merge': GitMerge,
  'arrow-down': ArrowDown,
  'bomb': Bomb,
  'frown': Frown,
  'megaphone': Megaphone,
  'sparkles': Sparkles,
  'trophy': Trophy,
};

export function BonusIcon({ name, className }: { name: string; className?: string }) {
  const Icon = ICON_MAP[name] ?? Trophy;
  return <Icon className={className} />;
}

export function BonusRow({
  bonus,
  isCommissioner,
  onEdit,
  onDelete,
  onAward,
}: {
  bonus: LeagueBonus;
  isCommissioner: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onAward: () => void;
}) {
  const isManual = bonus.trigger_type === 'manual';
  const isWeekly = bonus.frequency === 'per_week';
  const cadenceLabel =
    bonus.frequency === 'per_week'
      ? 'Weekly'
      : bonus.frequency === 'per_season'
        ? 'Season'
        : 'One-time';

  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-card',
        'px-4 py-3 sm:px-5 sm:py-4',
        'grid items-center gap-3 sm:gap-4',
        'grid-cols-[40px_1fr_auto] sm:grid-cols-[40px_1fr_auto]',
      )}
    >
      <div className="w-9 h-9 rounded-lg bg-orange-500/12 text-orange-500 flex items-center justify-center">
        <BonusIcon name={bonus.icon} className="w-4 h-4" />
      </div>

      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-bold text-sm">{bonus.bonus_name}</div>
          {bonus.is_default && (
            <Badge
              variant="outline"
              className="text-[9px] tracking-wider uppercase bg-blue-500/10 text-blue-400 border-blue-500/30 h-5"
            >
              Default
            </Badge>
          )}
          <span className="text-[10px] uppercase tracking-[0.06em] font-semibold text-muted-foreground bg-muted rounded-full px-2 py-0.5">
            {isManual ? 'Manual' : 'Auto'} · {cadenceLabel}
          </span>
        </div>
        <div className="text-xs text-muted-foreground mt-1 truncate">
          {bonus.description}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap justify-end">
        <div className="font-bold text-lg tabular-nums whitespace-nowrap">
          {fmtUSD(bonus.amount)}
          {isWeekly && (
            <span className="text-[11px] font-medium text-muted-foreground ml-0.5">
              /wk
            </span>
          )}
        </div>
        {/* Edit / Delete / Award gated behind commissioner. League members
            get clean read-only rows — cleaner than buttons that would
            silently fail at the RLS layer. */}
        {isCommissioner && (
          <div className="flex gap-1">
            {isManual && (
              <Button
                size="sm"
                onClick={onAward}
                className="h-8 px-3 text-xs gap-1"
              >
                <Send className="w-3 h-3" /> Award
              </Button>
            )}
            <Button
              size="icon"
              variant="ghost"
              onClick={onEdit}
              className="h-8 w-8"
              aria-label={`Edit ${bonus.bonus_name}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={onDelete}
              className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
              aria-label={`Delete ${bonus.bonus_name}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
